import type { ColumnDef } from "@gately/ui/data-table";
import type {
  AccountDisableReason,
  AccountStatus,
  AssignableGatelyRole,
  CountryCode,
  Terms,
} from "@gately/validators";
import { useEffect, useMemo, useState } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import {
  Ban,
  MoreHorizontal,
  Plus,
  RotateCcw,
  Send,
  Trash2,
} from "lucide-react";

import { Avatar } from "@gately/ui/avatar";
import { Button } from "@gately/ui/button";
import { DataTable, SortableHeader } from "@gately/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@gately/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@gately/ui/dropdown-menu";
import { Input } from "@gately/ui/input";
import { Label } from "@gately/ui/label";
import { PhoneInput } from "@gately/ui/phone-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@gately/ui/select";
import { StatusBadge } from "@gately/ui/status-badge";
import { toast } from "@gately/ui/toast";
import {
  accountDisableReasonLabels,
  accountDisableReasons,
  assignableGatelyRoles,
  gatelyRoles,
  parsePhone,
  requiredContact,
} from "@gately/validators";

import { orpc } from "~/lib/orpc";
import { useMe } from "~/lib/use-me";
import { resolvePermissions } from "~/lib/use-permissions";
import { activeOrgType, useTerms } from "~/lib/use-terms";

export const Route = createFileRoute("/_dashboard/accounts")({
  // `?new=1` opens the create dialog on arrival — the sidebar's "Inviter un
  // membre" shortcut lands here. The URL IS the dialog's open state, so the
  // shortcut works even when this page is already the current one. `?q=` holds
  // the server-side roster search so it survives a reload and a share.
  validateSearch: (
    search: Record<string, unknown>,
  ): { new?: true; q?: string } => ({
    ...(search.new === true || search.new === "1"
      ? { new: true as const }
      : {}),
    ...(typeof search.q === "string" && search.q.length > 0
      ? { q: search.q }
      : {}),
  }),
  // Manager-only: the roster is admin data (account.list is adminProcedure).
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(
      orpc.me.get.queryOptions(),
    );
    if (!resolvePermissions(me).canManage) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: "/" });
    }
  },
  // Prefetch so useSuspenseQuery has data ready and never suspends mid-navigation.
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(
        orpc.account.list.queryOptions({ input: { limit: 500 } }),
      ),
      context.queryClient.ensureQueryData(orpc.gate.list.queryOptions()),
    ]),
  component: AccountsPage,
});

// Role display names depend on the vertical: a residence has a syndic and des
// propriétaires, an office building has an admin and des responsables.
function roleLabels(terms: Terms): Record<string, string> {
  return {
    syndic: terms.adminLabel,
    proprietaire: terms.ownerLabel,
    gardien: terms.guardLabel,
    // Same word in both verticals: a tenant is a tenant whether the org is a
    // résidence or an immeuble de bureaux, so this one stays out of Terms.
    locataire: "Locataire",
  };
}

function deriveStatus(a: {
  role: string;
  lastSignInAt: string | Date | null;
  dutyStatus: string | null;
}): AccountStatus {
  if (a.role === "gardien") {
    return a.dutyStatus === "on_duty" ? "on_duty" : "off_duty";
  }
  return a.lastSignInAt ? "active" : "invited";
}

function StatusCell({ status }: { status: AccountStatus }) {
  if (status === "invited") {
    return <StatusBadge tone="warning">Invité</StatusBadge>;
  }
  if (status === "active") {
    return <StatusBadge tone="success">Actif</StatusBadge>;
  }
  const onDuty = status === "on_duty";
  return (
    <StatusBadge tone={onDuty ? "success" : "neutral"}>
      {onDuty ? "En service" : "Hors service"}
    </StatusBadge>
  );
}

function AccountsPage() {
  const queryClient = useQueryClient();
  // Held in the URL rather than in state — see `validateSearch` above.
  const routeSearch = Route.useSearch();
  const navigate = Route.useNavigate();

  // Debounced free-text search: local state → URL → server after 300 ms.
  const [q, setQ] = useState(routeSearch.q ?? "");
  useEffect(() => {
    const t = setTimeout(() => {
      const next = q.trim() || undefined;
      if (next !== routeSearch.q) {
        void navigate({
          search: (prev) => ({ ...prev, q: next }),
          replace: true,
        });
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q, routeSearch.q, navigate]);

  const accountsQuery = useQuery({
    ...orpc.account.list.queryOptions({
      input: { limit: 500, ...(routeSearch.q ? { q: routeSearch.q } : {}) },
    }),
    placeholderData: keepPreviousData,
  });
  const accounts = useMemo(
    () => accountsQuery.data ?? [],
    [accountsQuery.data],
  );
  const { data: gates } = useSuspenseQuery(orpc.gate.list.queryOptions());
  const me = useMe();
  const terms = useTerms();
  const isOffice = me.activeOrg?.type === "office";
  const labels = useMemo(() => roleLabels(terms), [terms]);
  const createOpen = routeSearch.new === true;
  const setCreateOpen = (open: boolean) =>
    void navigate({
      search: (prev) => ({ ...prev, new: open ? (true as const) : undefined }),
      replace: true,
    });
  const [toRemove, setToRemove] = useState<
    { userId: string; name: string } | undefined
  >(undefined);

  const [name, setName] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [country, setCountry] = useState<CountryCode>("MA");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<AssignableGatelyRole>("proprietaire");

  const e164 = parsePhone(identifier, country);
  const phoneValid = e164 !== null;
  const phoneTouched = identifier.trim().length > 0;
  const emailTrimmed = email.trim();
  const emailTouched = emailTrimmed.length > 0;
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed);

  // Which identifier is REQUIRED (the other stays an optional extra). Same
  // rule the server enforces: residence → phone; office → email, except
  // gardiens (the gate app signs in by phone OTP only).
  const needContact = requiredContact(activeOrgType(me), role);
  const phoneOk =
    needContact === "phone" ? phoneValid : !phoneTouched || phoneValid;
  const emailOk =
    needContact === "email" ? emailValid : !emailTouched || emailValid;
  const canSubmitCreate = phoneOk && emailOk;

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.account.list.key() });

  const createAccount = useMutation(
    orpc.account.create.mutationOptions({
      onSuccess: async (data) => {
        toast.success(
          data.reused
            ? `Compte existant « ${data.existingName} » ajouté à ${terms.thisOrg}.`
            : "Compte créé.",
        );
        setName("");
        setIdentifier("");
        setCountry("MA");
        setEmail("");
        setRole("proprietaire");
        setCreateOpen(false);
        await invalidate();
      },
      onError: (err) =>
        toast.error(err.message || "Impossible de créer le compte."),
    }),
  );
  const removeAccount = useMutation(
    orpc.account.remove.mutationOptions({
      onSuccess: async () => {
        toast.success("Compte retiré.");
        setToRemove(undefined);
        await invalidate();
      },
      onError: (err) => toast.error(err.message || "Échec du retrait."),
    }),
  );
  const resendInvite = useMutation(
    orpc.account.resendInvite.mutationOptions({
      onSuccess: () => toast.success("Invitation renvoyée."),
      onError: (err) => toast.error(err.message || "Échec de l'envoi."),
    }),
  );
  const setGates = useMutation(
    orpc.account.setGates.mutationOptions({
      onSuccess: async () => {
        toast.success("Portails mis à jour.");
        await invalidate();
      },
      onError: (err) =>
        toast.error(err.message || "Échec de la mise à jour des portails."),
    }),
  );

  const [toDisable, setToDisable] = useState<
    { userId: string; name: string } | undefined
  >(undefined);
  const [disableReason, setDisableReason] =
    useState<AccountDisableReason>("unpaid_dues");
  const [disableNote, setDisableNote] = useState("");

  const disableAccount = useMutation(
    orpc.account.disable.mutationOptions({
      onSuccess: async () => {
        toast.success("Compte désactivé.");
        setToDisable(undefined);
        setDisableReason("unpaid_dues");
        setDisableNote("");
        await invalidate();
      },
      onError: (err) =>
        toast.error(err.message || "Échec de la désactivation."),
    }),
  );
  const enableAccount = useMutation(
    orpc.account.enable.mutationOptions({
      onSuccess: async () => {
        toast.success("Compte réactivé.");
        await invalidate();
      },
      onError: (err) => toast.error(err.message || "Échec de la réactivation."),
    }),
  );

  const [pinFor, setPinFor] = useState<{ userId: string; name: string } | null>(
    null,
  );
  const [pinValue, setPinValue] = useState("");

  const setPin = useMutation(
    orpc.gateDevice.setPin.mutationOptions({
      onSuccess: () => {
        toast.success(
          isOffice
            ? "Code PIN défini. L'agent d'accueil devra le changer à la 1ʳᵉ utilisation."
            : "Code PIN défini. Le concierge devra le changer à la 1ʳᵉ utilisation.",
        );
        setPinFor(null);
        setPinValue("");
      },
      onError: (e) =>
        toast.error(e.message || "Impossible de définir le code PIN."),
    }),
  );

  // Nothing pre-filters client-side any more: free text is matched by the
  // server (name, phone, email AND unit label), and rôle/statut are grid
  // filter chips. Re-filtering text here on name+contact alone would drop rows
  // the server legitimately matched.
  type AccountRow = (typeof accounts)[number];

  // Memoised: TanStack's flexRender treats each `cell` function as a component
  // type, so a fresh columns array on every render would remount every cell —
  // closing an open actions dropdown mid-interaction (gate checkboxes rely on
  // the menu staying open). Only stable references go in the dep list; the
  // mutations are read via their referentially-stable `.mutate`.
  // `mutate` is referentially stable across a mutation's lifecycle, unlike the
  // mutation object itself — depending on the object would rebuild `columns`
  // on every pending/settled transition and remount the open dropdown.
  const { mutate: resendInviteMutate } = resendInvite;
  const { mutate: setGatesMutate } = setGates;
  const { mutate: enableAccountMutate } = enableAccount;

  const columns: ColumnDef<AccountRow>[] = useMemo(
    () => [
      {
        id: "name",
        accessorKey: "name",
        size: 280,
        minSize: 200,
        enableHiding: false,
        meta: { label: "Nom", filter: { kind: "text", placeholder: "Nom…" } },
        header: ({ column }) => (
          <SortableHeader column={column}>Nom</SortableHeader>
        ),
        cell: ({ row }) => {
          const a = row.original;
          const subtitle =
            a.userId === me.user.id
              ? "Vous"
              : a.unitLabel
                ? `${terms.unitRowLabel} ${a.unitLabel}`
                : null;
          return (
            <div className="flex min-w-0 items-center gap-3">
              <Avatar name={a.name} />
              <div className="min-w-0">
                <div className="truncate font-medium">{a.name}</div>
                {subtitle ? (
                  <div className="text-muted-foreground truncate text-xs">
                    {subtitle}
                  </div>
                ) : null}
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "role",
        size: 160,
        minSize: 130,
        filterFn: "arrIncludesSome",
        meta: { label: "Rôle" },
        header: ({ column }) => (
          <SortableHeader column={column}>Rôle</SortableHeader>
        ),
        cell: ({ row }) => (
          <StatusBadge
            tone={row.original.role === "syndic" ? "brand" : "info"}
            dot={false}
          >
            {labels[row.original.role] ?? row.original.role}
          </StatusBadge>
        ),
      },
      {
        id: "contact",
        accessorFn: (a) => `${a.phone ?? ""} ${a.email ?? ""}`,
        size: 240,
        minSize: 170,
        meta: {
          label: "Contact",
          filter: { kind: "text", placeholder: "Tél. ou e-mail" },
        },
        header: "Contact",
        cell: ({ row }) => {
          const a = row.original;
          return (
            <div className="text-muted-foreground min-w-0 text-sm">
              {a.phone ? <div className="truncate">{a.phone}</div> : null}
              {a.email ? (
                <div className="truncate text-xs">{a.email}</div>
              ) : null}
              {!a.phone && !a.email ? "—" : null}
            </div>
          );
        },
      },
      {
        id: "status",
        accessorFn: (a) => (a.disabledAt ? "disabled" : deriveStatus(a)),
        size: 170,
        minSize: 130,
        filterFn: "arrIncludesSome",
        meta: { label: "Statut" },
        header: ({ column }) => (
          <SortableHeader column={column}>Statut</SortableHeader>
        ),
        cell: ({ row }) => {
          const a = row.original;
          return a.disabledAt ? (
            <div
              title={
                a.disabledReason
                  ? accountDisableReasonLabels[
                      a.disabledReason as AccountDisableReason
                    ] + (a.disabledNote ? ` — ${a.disabledNote}` : "")
                  : undefined
              }
            >
              <StatusBadge tone="danger" dot={false}>
                Désactivé
              </StatusBadge>
            </div>
          ) : (
            <StatusCell status={deriveStatus(a)} />
          );
        },
      },
      {
        id: "actions",
        size: 70,
        minSize: 60,
        enableSorting: false,
        enableHiding: false,
        enableResizing: false,
        meta: { label: "Actions", align: "right" },
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => {
          const a = row.original;
          if (a.userId === me.user.id) return null;
          const status = deriveStatus(a);
          return (
            <div className="flex justify-end">
              <DropdownMenu>
                <DropdownMenuTrigger
                  aria-label={`Actions pour ${a.name}`}
                  className="hover:bg-muted flex size-8 items-center justify-center rounded-lg outline-none"
                >
                  <MoreHorizontal className="size-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  {status === "invited" ? (
                    <DropdownMenuItem
                      onClick={() => resendInviteMutate({ userId: a.userId })}
                    >
                      <Send className="size-4" />
                      Renvoyer l'invitation
                    </DropdownMenuItem>
                  ) : null}
                  {a.role === "gardien" ? (
                    <>
                      <DropdownMenuLabel>Portails</DropdownMenuLabel>
                      {gates.map((g) => {
                        const checked = a.assignedGateIds.includes(g.id);
                        return (
                          <DropdownMenuCheckboxItem
                            key={g.id}
                            checked={checked}
                            onSelect={(e) => e.preventDefault()}
                            onCheckedChange={(next) => {
                              const ids = next
                                ? [...a.assignedGateIds, g.id]
                                : a.assignedGateIds.filter((id) => id !== g.id);
                              setGatesMutate({
                                userId: a.userId,
                                gateIds: ids,
                              });
                            }}
                          >
                            {g.name}
                          </DropdownMenuCheckboxItem>
                        );
                      })}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onSelect={(e) => {
                          e.preventDefault();
                          setPinFor({ userId: a.userId, name: a.name });
                          setPinValue("");
                        }}
                      >
                        Définir le code PIN
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  ) : null}
                  {a.role === "proprietaire" ? (
                    a.disabledAt ? (
                      <DropdownMenuItem
                        onClick={() =>
                          enableAccountMutate({ userId: a.userId })
                        }
                      >
                        <RotateCcw className="size-4" />
                        Réactiver le compte
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem
                        onClick={() =>
                          setToDisable({ userId: a.userId, name: a.name })
                        }
                      >
                        <Ban className="size-4" />
                        Désactiver le compte
                      </DropdownMenuItem>
                    )
                  ) : null}
                  <DropdownMenuItem
                    variant="destructive"
                    onClick={() =>
                      setToRemove({ userId: a.userId, name: a.name })
                    }
                  >
                    <Trash2 className="size-4" />
                    Retirer {terms.ofTheOrg}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ],
    [
      me.user.id,
      terms,
      labels,
      gates,
      resendInviteMutate,
      setGatesMutate,
      enableAccountMutate,
      setPinFor,
      setPinValue,
      setToDisable,
      setToRemove,
    ],
  );

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmitCreate) return;
    createAccount.mutate({
      name,
      role,
      phone: phoneTouched && e164 ? e164 : undefined,
      email: emailTouched ? emailTrimmed : undefined,
    });
  }

  return (
    <div className="px-7 py-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Comptes</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {accounts.length} compte{accounts.length > 1 ? "s" : ""} sur{" "}
            {me.activeOrg?.name ?? `votre ${terms.orgNoun}`}
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="size-4" />
          Créer un compte
        </Button>
      </div>

      <DataTable
        className="mt-4"
        columns={columns}
        data={accounts}
        isLoading={accountsQuery.isPending}
        pageSize={25}
        initialPinning={{ left: ["name"], right: ["actions"] }}
        showColumnFilters
        showColumnToggle
        search={{
          value: q,
          onChange: setQ,
          placeholder: "Rechercher un compte…",
        }}
        filters={[
          {
            columnId: "role",
            label: "Rôle",
            options: gatelyRoles.map((r) => ({
              value: r,
              label: labels[r] ?? r,
            })),
          },
          {
            columnId: "status",
            label: "Statut",
            options: [
              { value: "active", label: "Actif" },
              { value: "invited", label: "Invité" },
              { value: "on_duty", label: "En service" },
              { value: "off_duty", label: "Hors service" },
              { value: "disabled", label: "Désactivé" },
            ],
          },
        ]}
        empty={{ title: "Aucun compte ne correspond." }}
      />

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Créer un compte</DialogTitle>
            <DialogDescription>
              {needContact === "email"
                ? `Ajoutez un membre à votre ${terms.orgNoun}. Il recevra un lien d'activation par e-mail et se connectera avec Google ou Microsoft.`
                : `Ajoutez un membre à votre ${terms.orgNoun}. Il se connectera ensuite avec un code à usage unique.`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="name">Nom complet</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Fatima Zahra El Amrani"
                required
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="role">Rôle</Label>
              <Select
                value={role}
                onValueChange={(v) => setRole(v as AssignableGatelyRole)}
              >
                <SelectTrigger id="role" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* `locataire` is deliberately excluded: it is only ever
                      created by occupancy.declare, never hand-assigned here. */}
                  {assignableGatelyRoles.map((r) => (
                    <SelectItem key={r} value={r}>
                      {labels[r]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Required identifier first; the optional one follows. */}
            {(() => {
              const phoneField = (
                <div key="phone" className="flex flex-col gap-2">
                  <Label htmlFor="identifier">
                    {needContact === "phone"
                      ? "Numéro de téléphone"
                      : "Numéro de téléphone (optionnel)"}
                  </Label>
                  <PhoneInput
                    id="identifier"
                    country={country}
                    onCountryChange={setCountry}
                    value={identifier}
                    onChange={setIdentifier}
                    invalid={phoneTouched && !phoneValid}
                    required={needContact === "phone"}
                  />
                  {phoneTouched && !phoneValid ? (
                    <p className="text-destructive text-sm">
                      Numéro de téléphone invalide.
                    </p>
                  ) : null}
                </div>
              );
              const emailField = (
                <div key="email" className="flex flex-col gap-2">
                  <Label htmlFor="account-email">
                    {needContact === "email" ? "E-mail" : "E-mail (optionnel)"}
                  </Label>
                  <Input
                    id="account-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="prenom.nom@entreprise.ma"
                    aria-invalid={emailTouched && !emailValid}
                    required={needContact === "email"}
                  />
                  {emailTouched && !emailValid ? (
                    <p className="text-destructive text-sm">
                      Adresse e-mail invalide.
                    </p>
                  ) : null}
                </div>
              );
              return needContact === "email"
                ? [emailField, phoneField]
                : [phoneField, emailField];
            })()}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreateOpen(false)}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={createAccount.isPending || !canSubmitCreate}
              >
                {createAccount.isPending ? "Création…" : "Créer le compte"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={toRemove !== undefined}
        onOpenChange={(open) => {
          if (!open) setToRemove(undefined);
        }}
      >
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <span className="text-destructive mb-2 flex size-10 items-center justify-center rounded-xl bg-(--danger-50)">
              <Trash2 className="size-5" strokeWidth={1.85} />
            </span>
            <DialogTitle className="text-lg">Retirer ce compte ?</DialogTitle>
            <DialogDescription>
              <b className="text-foreground/80 font-semibold">
                {toRemove?.name}
              </b>{" "}
              sera retiré {terms.ofTheOrg}. Son compte Gately n'est pas
              supprimé.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setToRemove(undefined)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              disabled={removeAccount.isPending}
              onClick={() => {
                if (toRemove) removeAccount.mutate({ userId: toRemove.userId });
              }}
            >
              {removeAccount.isPending ? "Retrait…" : "Retirer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={toDisable !== undefined}
        onOpenChange={(open) => {
          if (!open) setToDisable(undefined);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Désactiver ce compte ?</DialogTitle>
            <DialogDescription>
              <b className="text-foreground/80 font-semibold">
                {toDisable?.name}
              </b>{" "}
              ne pourra plus créer d'invitation. Le motif ci-dessous lui sera
              affiché. Vous pourrez réactiver le compte à tout moment.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="disable-reason">Motif</Label>
              <Select
                value={disableReason}
                onValueChange={(v) =>
                  setDisableReason(v as AccountDisableReason)
                }
              >
                <SelectTrigger id="disable-reason" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {accountDisableReasons.map((r) => (
                    <SelectItem key={r} value={r}>
                      {accountDisableReasonLabels[r]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="disable-note">Note (facultatif)</Label>
              <Input
                id="disable-note"
                value={disableNote}
                onChange={(e) => setDisableNote(e.target.value.slice(0, 500))}
                placeholder="Ex. : montant ou référence"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setToDisable(undefined)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              disabled={disableAccount.isPending}
              onClick={() => {
                if (toDisable)
                  disableAccount.mutate({
                    userId: toDisable.userId,
                    reason: disableReason,
                    note: disableNote.trim() || undefined,
                  });
              }}
            >
              {disableAccount.isPending ? "Désactivation…" : "Désactiver"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={pinFor !== null}
        onOpenChange={(o) => !o && setPinFor(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Code PIN — {pinFor?.name}</DialogTitle>
            <DialogDescription>
              4 à 6 chiffres. {isOffice ? "L'agent d'accueil" : "Le concierge"}{" "}
              sera invité à le changer lors de sa première prise de service.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={pinValue}
            onChange={(e) =>
              setPinValue(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
            inputMode="numeric"
            placeholder="••••"
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPinFor(null)}>
              Annuler
            </Button>
            <Button
              disabled={pinValue.length < 4 || setPin.isPending}
              onClick={() =>
                pinFor &&
                setPin.mutate({ userId: pinFor.userId, pin: pinValue })
              }
            >
              {setPin.isPending ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
